import React, { useState, useEffect, useRef } from 'react'
import Modal from '../shared/Modal'
import BulkEditConfirmationModal from './BulkEditConfirmationModal'
import { Aircraft, Transaction, Event } from '../../types/intellijetnet'
import { AircraftOverride, OffMarket, MarketCustomColumn, TransactionOverride } from '../../types/database'
import { ExportOptions } from './ExportOptionsModal'
import { formatPrice, formatAskingPrice, formatTakePrice, formatModifier, formatDate, formatRelativeValue as formatRelativeValueUtil } from '../../utils/formatters'
import { getEffectiveAircraftData } from '../../utils/overrides'
import { isUnderContract } from '../../utils/change-detection'
import { getCellAlignment, getAlignmentClass } from '../../utils/cell-alignment'
import { PDFExportService } from '../../services/pdf-export.service'
import { ReportConfig } from './PDFReportRenderer'
import { OptionLeadersList } from '../aircraft/OptionLeadersList'
import { RelativeValueBreakdown } from '../../hooks/useRelativeValue'
import { MarketStatistics } from '../../utils/market-statistics'
import MarketOverviewPage from '../market/MarketOverviewPage'
// Import legacy report styling - scoped under .ij-report-root
import '../../styles/pdf-report-legacy.css'
// Import IntelliJet logo for cover page
import intellijetLogoPurple from '../../assets/branding/intellijet-logo-purple.png'
// Extracted sub-components and utilities
import { EditableCell } from './pdf-preview/EditableCell'
import HideAircraftDropdown from './pdf-preview/HideAircraftDropdown'
import PreparedForPrompt from './pdf-preview/PreparedForPrompt'
import PDFOptionsEditor from './pdf-preview/PDFOptionsEditor'
import { getModelShortPlural, formatCoverDate, getOwnerValue, getBrokerValue, formatDamageHistory, renderOptions, formatRelativeValue, formatDateListed, formatAskingTake, formatSoldDates, formatSoldPrices } from './pdf-preview/preview-formatters'
import { getEnabledColumns } from './pdf-preview/column-config'
import { calculateColumnWidths } from './pdf-preview/column-widths'
import { CombinedAircraftData, MarketTableData, PreviewRowData } from './pdf-preview/types'

// Re-export for consumers that imported from here
export type { RelativeValueBreakdown }

interface PDFPreviewModalProps {
  isOpen: boolean
  onClose: () => void
  onDownload: (editedData: Map<number, { owner?: string; broker?: string }>) => void
  onUpdateOptions?: (options: ExportOptions) => Promise<void> | void
  tables: MarketTableData[]
  overrides: Map<number, AircraftOverride>
  options: Map<number, Array<{ name: string; value: number }>>
  relativeValues: Map<number, number | null>
  relativeValueBreakdowns?: Map<number, RelativeValueBreakdown>
  missingABB: Set<number>
  exportOptions: ExportOptions
  customColumns?: MarketCustomColumn[]
  companies?: Map<number, { company_name: string }>
  transactionOverrides?: Map<string, TransactionOverride>
  transactionOptionDetails?: Map<string, { name: string; value: number }>
  modelName: string
  makeName: string
  manufacturer?: string | null
  marketStatistics?: MarketStatistics
}

export default function PDFPreviewModal({
  isOpen,
  onClose,
  onDownload,
  onUpdateOptions,
  tables,
  overrides,
  options,
  relativeValues,
  relativeValueBreakdowns,
  missingABB,
  exportOptions,
  customColumns = [],
  companies,
  transactionOverrides,
  transactionOptionDetails,
  modelName,
  makeName,
  manufacturer,
  marketStatistics
}: PDFPreviewModalProps) {
  const [editedData, setEditedData] = useState<Map<number, { owner?: string; broker?: string }>>(new Map())
  const [previewData, setPreviewData] = useState<{ [tableType: string]: PreviewRowData[] }>({})
  const [showOptionsEditor, setShowOptionsEditor] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [hiddenAircraftIds, setHiddenAircraftIds] = useState<Set<number>>(new Set())
  const reportContainerRef = useRef<HTMLDivElement>(null)
  const [bulkEditConfirmation, setBulkEditConfirmation] = useState<{
    isOpen: boolean
    aircraftId: number
    field: 'owner' | 'broker'
    originalValue: string
    newValue: string
    matchingAircraft: Array<{ aircraftId: number; serial: string }>
  } | null>(null)
  const [showPreparedForPrompt, setShowPreparedForPrompt] = useState(false)

  // Initialize preview data when modal opens
  // Note: preparePreviewData is intentionally not in deps - it's defined below and uses the same deps
  useEffect(() => {
    if (isOpen) {
      const newPreviewData: { [tableType: string]: PreviewRowData[] } = {}

      tables.forEach(tableData => {
        newPreviewData[tableData.type] = preparePreviewData(tableData)
      })

      setPreviewData(newPreviewData)
      setEditedData(new Map()) // Reset edits when modal opens
      setHiddenAircraftIds(new Set()) // Reset hidden aircraft when modal opens
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, tables, overrides, options, relativeValues, missingABB, exportOptions, customColumns, companies])

  // Helper function to filter tables based on section visibility options
  const getVisibleTables = (tablesToFilter: MarketTableData[]) => {
    if (exportOptions.currentMarketOnly) {
      return tablesToFilter.filter(t => t.type === 'for-sale')
    }
    if (exportOptions.hideRemovedFromMarket) {
      return tablesToFilter.filter(t => t.type !== 'removed')
    }
    return tablesToFilter
  }

  const preparePreviewData = (tableData: MarketTableData): PreviewRowData[] => {
    // Sort data based on table type
    const sortedData = [...tableData.data].sort((a, b) => {
      if (tableData.type === 'sold') {
        // Sort sold data by date sold descending (most recent first)
        const dateA = a.aircraftTransaction?.transaction_date || a.transaction?.trans_date || '1900-01-01'
        const dateB = b.aircraftTransaction?.transaction_date || b.transaction?.trans_date || '1900-01-01'
        // Descending order (most recent first)
        return dateB.localeCompare(dateA)
      }
      if (tableData.type === 'removed') {
        // Sort removed data by date removed descending (most recent first)
        const dateA = a.event?.date || '1900-01-01'
        const dateB = b.event?.date || '1900-01-01'
        // Descending order (most recent first)
        return dateB.localeCompare(dateA)
      }
      // Default: sort by serial number
      const serialA = a.aircraft.sernbr || ''
      const serialB = b.aircraft.sernbr || ''
      return serialA.localeCompare(serialB, undefined, { numeric: true })
    })

    return sortedData.flatMap(combinedItem => {
      const { aircraft, source, transaction, event, aircraftTransaction } = combinedItem

      // Add safety check for aircraft data
      if (!aircraft) {
        return []
      }

      const override = overrides.get(aircraft.aircraft_id)
      const effectiveAircraft = getEffectiveAircraftData(aircraft, override)

      // Check if aircraft is off-market: either source is 'offMarket' or status override is 'Off Market'
      // This handles both native off-market aircraft AND moved aircraft marked as off-market
      const isStatusOffMarket = tableData.type === 'for-sale' && effectiveAircraft.status === 'Off Market'
      const isOffMarket = source === 'offMarket' || isStatusOffMarket

      // Filter out off-market aircraft if hideOffMarket option is enabled
      if (exportOptions.hideOffMarket && isOffMarket) return []

      // For sold aircraft, use transaction overrides for options
      let aircraftOptions: Array<{ name: string; value: number }> = []
      if (tableData.type === 'sold' && aircraftTransaction?.transaction_id) {
        const transactionId = aircraftTransaction.transaction_id
        const override = transactionOverrides?.get(transactionId)
        // Use override options if available, otherwise fall back to aircraftTransaction options
        const optionIds = override?.options || aircraftTransaction?.options || []
        if (optionIds.length > 0 && transactionOptionDetails) {
          aircraftOptions = optionIds
            .map((id: string) => transactionOptionDetails.get(id))
            .filter((opt: { name: string; value: number } | undefined): opt is { name: string; value: number } => !!opt)
        }
      } else {
        // For non-sold aircraft, use the regular options map
        aircraftOptions = options.get(aircraft.aircraft_id) || []
      }

      // Get edited values or original values
      const edits = editedData.get(aircraft.aircraft_id)

      const baseData: PreviewRowData = {
        aircraftId: aircraft.aircraft_id,
        serial: `${aircraft.sernbr || ''}\n${aircraft.regnbr || ''}`,
        owner: edits?.owner || getOwnerValue(aircraft.aircraft_id, overrides, companies),
        broker: edits?.broker || getBrokerValue(aircraft.aircraft_id, overrides, companies),
        year: `${effectiveAircraft.yearmfr || ''}\n${effectiveAircraft.yeardlv || ''}`,
        aftt: (() => {
          const aftt = effectiveAircraft.aftt
          if (aftt === null || aftt === undefined) return '-'
          if (typeof aftt === 'number') return aftt.toLocaleString()
          // Try to convert string to number
          const numAftt = Number(aftt)
          if (!isNaN(numAftt)) return numAftt.toLocaleString()
          return String(aftt) // Fallback to string representation
        })(),
        emp: effectiveAircraft.emp || '-',
        damage: formatDamageHistory(effectiveAircraft.damagehistoryyn),
        pax: effectiveAircraft.pax != null ? String(effectiveAircraft.pax) : '-',
        paintInterior: `${effectiveAircraft.paint_date ?? '-'}\n${effectiveAircraft.interior_date ?? '-'}`,
        options: renderOptions(aircraftOptions),
        relativeValue: formatRelativeValue(aircraft.aircraft_id, relativeValues, relativeValueBreakdowns, missingABB)
      }

      // Add custom column values
      customColumns.forEach((column) => {
        const override = overrides.get(aircraft.aircraft_id)
        const fieldName = `custom_${column.column_index}_value` as keyof AircraftOverride
        const value = override?.[fieldName] as string | null
        baseData[`custom${column.column_index}`] = value || '-'
      })

      // Add type-specific data
      switch (tableData.type) {
        case 'for-sale':
          baseData.dateListed = isOffMarket ? 'Off Market' : formatDateListed(effectiveAircraft.datelisted)
          baseData.askingTake = formatAskingTake(aircraft.aircraft_id, effectiveAircraft, overrides, exportOptions)
          break
        case 'sold':
          baseData.dates = formatSoldDates(effectiveAircraft.datelisted, transaction?.trans_date || null, aircraftTransaction)
          baseData.prices = formatSoldPrices(aircraft.aircraft_id, effectiveAircraft, transaction || null, overrides, exportOptions, aircraftTransaction)
          break
        case 'removed':
          baseData.dateRemoved = event?.date ? formatDate(event.date) : '-'
          break
      }

      return [baseData]
    })
  }

  const findMatchingAircraft = (field: 'owner' | 'broker', value: string, excludeAircraftId: number) => {
    const matching: Array<{ aircraftId: number; serial: string }> = []

    Object.values(previewData).flat().forEach(row => {
      if (row.aircraftId !== excludeAircraftId && row[field] === value) {
        matching.push({
          aircraftId: row.aircraftId,
          serial: row.serial.split('\n')[0] // Get just the serial number part
        })
      }
    })

    return matching
  }

  const handleBulkEditRequest = (aircraftId: number, field: 'owner' | 'broker', originalValue: string, newValue: string) => {
    const matchingAircraft = findMatchingAircraft(field, originalValue, aircraftId)

    if (matchingAircraft.length > 0) {
      // Show bulk edit confirmation
      setBulkEditConfirmation({
        isOpen: true,
        aircraftId,
        field,
        originalValue,
        newValue,
        matchingAircraft
      })
    } else {
      // No matches, just apply the change
      handleCellEdit(aircraftId, field, newValue)
    }
  }

  const handleBulkEditConfirm = (applyToAll: boolean) => {
    if (!bulkEditConfirmation) return

    const { aircraftId, field, newValue, matchingAircraft } = bulkEditConfirmation

    if (applyToAll) {
      // Apply to all matching aircraft
      const aircraftIds = [aircraftId, ...matchingAircraft.map(a => a.aircraftId)]
      aircraftIds.forEach(id => {
        handleCellEdit(id, field, newValue)
      })
    } else {
      // Apply only to the current aircraft
      handleCellEdit(aircraftId, field, newValue)
    }

    setBulkEditConfirmation(null)
  }

  const handleCellEdit = (aircraftId: number, field: 'owner' | 'broker', value: string) => {
    setEditedData(prev => {
      const newMap = new Map(prev)
      const existing = newMap.get(aircraftId) || {}
      newMap.set(aircraftId, { ...existing, [field]: value })
      return newMap
    })

    // Update preview data
    setPreviewData(prev => {
      const newData = { ...prev }
      Object.keys(newData).forEach(tableType => {
        newData[tableType] = newData[tableType].map(row =>
          row.aircraftId === aircraftId ? { ...row, [field]: value } : row
        )
      })
      return newData
    })
  }

  const handleDownloadClick = () => {
    // Check if preparedFor is empty - if so, show the prompt
    if (!exportOptions.preparedFor || exportOptions.preparedFor.trim() === '') {
      setShowPreparedForPrompt(true)
      return
    }
    // Otherwise proceed with download
    performDownload()
  }

  const handlePreparedForSubmit = async (preparedForValue: string) => {
    if (!preparedForValue.trim()) return

    // Update export options with the new preparedFor value
    const updatedOptions = { ...exportOptions, preparedFor: preparedForValue.trim() }
    if (onUpdateOptions) {
      await onUpdateOptions(updatedOptions)
    }
    setShowPreparedForPrompt(false)

    // Wait a moment for the DOM to update with new preparedFor value, then download
    setTimeout(() => {
      performDownload()
    }, 100)
  }

  const performDownload = async () => {
    if (!reportContainerRef.current) {
      // Fallback to legacy method if ref not available
      onDownload(editedData)
      return
    }

    setIsExporting(true)
    try {
      // Build the ReportConfig from current state
      const config: ReportConfig = {
        modelName,
        makeName,
        manufacturer,
        marketStatistics,
        tables,
        overrides,
        options,
        relativeValues,
        missingABB,
        exportOptions,
        customColumns,
        companies,
        editedData,
        hiddenAircraftIds: hiddenAircraftIds
      }

      // Use html2canvas-based export for pixel-perfect PDF
      await PDFExportService.exportFromElement(reportContainerRef.current, config)
    } catch (error) {
      // Fallback to legacy method
      onDownload(editedData)
    } finally {
      setIsExporting(false)
    }
  }

  if (!isOpen) return null

  return (
    <Modal isOpen={isOpen} onClose={isExporting ? () => {} : onClose} title={`PDF Preview - ${makeName} ${modelName} (Legal Landscape)`} size="full">
      {/* Full-screen loading overlay for PDF generation */}
      {isExporting && (
        <div className="fixed inset-0 bg-white bg-opacity-90 flex items-center justify-center z-[100]">
          <div className="text-center">
            <div className="inline-flex items-center">
              <svg className="animate-spin -ml-1 mr-3 h-8 w-8 text-purple-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="text-lg text-gray-600">Generating PDF...</span>
            </div>
          </div>
        </div>
      )}
      <div className="flex flex-col h-full">
        {/* Header with export info */}
        <div className="bg-gray-50 p-4 rounded-lg mb-4 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div>
              <h4 className="font-medium text-gray-900">Export Configuration</h4>
              <p className="text-sm text-gray-600">
                Pricing Mode: <span className="font-medium">{exportOptions.pricingMode === 'internal' ? 'Actual Ask / Take' : 'Adjusted Ask / Take'}</span>
                {' • '}Historical Data: <span className="font-medium">{exportOptions.historicalMonths} months</span>
                {' • '}Format: <span className="font-medium">Legal Landscape (14" × 8.5")</span>
              </p>
            </div>
            <div className="flex items-center gap-4">
              {/* Hide Aircraft Dropdown - shows all aircraft from visible sections */}
              <HideAircraftDropdown
                tables={tables}
                overrides={overrides}
                exportOptions={exportOptions}
                hiddenAircraftIds={hiddenAircraftIds}
                onHiddenAircraftChange={setHiddenAircraftIds}
                getVisibleTables={getVisibleTables}
              />
              <div className="text-right">
                <p className="text-sm text-gray-600">
                  Tables: {getVisibleTables(tables).length} | Total Aircraft: {getVisibleTables(tables).reduce((sum, t) => sum + (previewData[t.type]?.length || 0), 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* PDF Preview Container with Legal Landscape Aspect Ratio - scrollable */}
        <div className="flex-1 overflow-auto min-h-0">
          <div className="flex justify-center py-4 px-4">
            {/* Legal Landscape Preview - ij-report-root for legacy print styling */}
            <div
              ref={reportContainerRef}
              className="ij-report-root flex flex-col"
              style={{
                width: '14in',
                minHeight: '8.5in',
                marginBottom: '4rem'
              }}
            >
              {/* Cover Page - Only show when Market Overview is excluded */}
              {exportOptions.marketOverview === 'exclude' && (
                <div className="ij-cover-page p-6 flex-shrink-0" style={{ minHeight: '8.5in', display: 'flex', flexDirection: 'column' }}>
                  {/* Top-left metadata block */}
                  <div className="text-left mb-8">
                    <h1 className="text-xl font-bold mb-2" style={{ color: '#000000' }}>
                      {makeName} {modelName} Market Summary - {formatCoverDate()}
                    </h1>
                    <div className="text-base font-bold" style={{ color: '#000000' }}>
                      Prepared for: {exportOptions.preparedFor || ''}
                    </div>
                  </div>

                  {/* Centered IntelliJet Logo - hero-sized */}
                  <div className="flex-1 flex items-center justify-center">
                    <img
                      src={intellijetLogoPurple}
                      alt="IntelliJet International"
                      style={{
                        maxWidth: '75%',
                        height: 'auto',
                        objectFit: 'contain'
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Market Overview Page - When included, this becomes the cover page with logo in top-left */}
              {exportOptions.marketOverview !== 'exclude' && marketStatistics && (
                <MarketOverviewPage
                  manufacturer={manufacturer || null}
                  makeName={makeName}
                  modelName={modelName}
                  statistics={marketStatistics}
                  isCoverPage={true}
                  logoSrc={intellijetLogoPurple}
                  preparedFor={exportOptions.preparedFor}
                  dateString={formatCoverDate()}
                />
              )}

              {/* Preview Tables - no internal scroll, let parent scroll */}
              {exportOptions.marketOverview !== 'only' && (
              <div className="p-4 pb-8 space-y-6">
          {/* Filter tables based on section visibility options */}
          {getVisibleTables(tables).map((tableData, tableIndex) => {
            const columns = getEnabledColumns(tableData.type, exportOptions, customColumns)
            const allRows = previewData[tableData.type] || []
            // Filter out individually hidden aircraft
            const rows = allRows.filter(row => !hiddenAircraftIds.has(row.aircraftId))
            const columnWidths = calculateColumnWidths(columns, rows)

            if (rows.length === 0) return null

            const modelShortPlural = getModelShortPlural(modelName)

            return (
              <div key={tableIndex} className="pdf-preview-container mb-8">
                {/* Section Header */}
                <div className="pdf-section-header">
                  {modelShortPlural} {tableData.title}
                </div>

                {/* Legend for Currently on Market */}
                {tableData.type === 'for-sale' && (
                  <div className="flex justify-center py-1 text-xs gap-2">
                    <div className="ij-legend-off-market px-4 py-0.5 font-bold text-center" style={{ border: '1px solid #000000', fontSize: '10px' }}>
                      Off Market
                    </div>
                    <div className="ij-legend-deal-pending px-4 py-0.5 font-bold text-center" style={{ border: '1px solid #000000', fontSize: '10px' }}>
                      Deal Pending
                    </div>
                  </div>
                )}

                <div className="ij-table-frame">
                    <div className="overflow-x-auto">
                      <table className="pdf-table ij-smart-widths">
                        {/* Dynamic column widths via colgroup */}
                        <colgroup>
                          {columnWidths.map((width, colIndex) => (
                            <col key={colIndex} style={{ width: `${width}%` }} />
                          ))}
                        </colgroup>
                        <thead>
                          <tr>
                            {columns.map((column, colIndex) => (
                              <th key={colIndex} className="pdf-table-header-cell">
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                                  {column.header.split('\n').map((line, lineIdx) => (
                                    <div key={lineIdx} style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>{line}</div>
                                  ))}
                                </div>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((row, rowIndex) => {
                            // Determine row background color based on section type and aircraft status
                            const getRowClassName = () => {
                              // SECTION-BASED COLORS: Sold and Removed sections ALWAYS use their section color
                              // regardless of Deal Pending or Off-Market status
                              if (tableData.type === 'sold') {
                                return 'ij-row-sold'
                              }
                              if (tableData.type === 'removed') {
                                return 'ij-row-removed'
                              }

                              // FOR-SALE SECTION: Apply status-based colors (Deal Pending, Off-Market)
                              const aircraftData = tables.find(t => t.type === tableData.type)?.data.find(d => d.aircraft.aircraft_id === row.aircraftId)
                              const override = overrides.get(row.aircraftId)
                              const effectiveAircraft = aircraftData ? getEffectiveAircraftData(aircraftData.aircraft, override) : null
                              const isOffMarket = aircraftData?.source === 'offMarket' || effectiveAircraft?.status === 'Off Market'
                              const isDealPending = aircraftData ? isUnderContract(aircraftData.aircraft, override) : false

                              if (isOffMarket && isDealPending) {
                                return 'ij-row-off-market-pending'
                              } else if (isOffMarket) {
                                return 'ij-row-off-market'
                              } else if (isDealPending) {
                                return 'ij-row-deal-pending'
                              }
                              return ''
                            }

                            return (
                              <tr key={rowIndex} className={getRowClassName()}>
                                {columns.map((column, colIndex) => {
                                  const cellValue = row[column.dataKey]
                                  // Options, relativeValue, askingTake, and prices columns return ReactNode, others return string
                                  const reactNodeColumns = ['options', 'relativeValue', 'askingTake', 'prices']
                                  const cellValueForAlignment = reactNodeColumns.includes(column.dataKey) ? '' : (cellValue as string)
                                  const alignment = getCellAlignment(cellValueForAlignment, column.dataKey)
                                  const alignmentClass = getAlignmentClass(alignment)
                                  // Add word-wrap class for Engine Prog column
                                  const wordWrapClass = column.dataKey === 'emp' ? 'break-words' : ''
                                  return (
                                    <td key={colIndex} className={`pdf-table-cell ${alignmentClass} ${wordWrapClass}`}>
                                      {column.editable ? (
                                        <EditableCell
                                          value={cellValue as string}
                                          onChange={(value: string) => handleCellEdit(row.aircraftId, column.dataKey as 'owner' | 'broker', value)}
                                          placeholder={`Enter ${column.header.toLowerCase()}`}
                                          aircraftId={row.aircraftId}
                                          field={column.dataKey as 'owner' | 'broker'}
                                          onBulkEditRequest={handleBulkEditRequest}
                                        />
                                      ) : (
                                        cellValue
                                      )}
                                    </td>
                                  )
                                })}
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
              </div>
            )
          })}
              </div>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between items-center pt-4 border-t border-gray-200 flex-shrink-0">
          <div className="text-sm text-gray-600">
            {editedData.size > 0 && (
              <span className="text-purple-600 font-medium">
                {editedData.size} aircraft with edits
              </span>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            >
              Cancel
            </button>
            {onUpdateOptions && (
              <button
                type="button"
                onClick={() => {
                  setShowOptionsEditor(true)
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>Edit PDF</span>
              </button>
            )}
            <button
              type="button"
              onClick={handleDownloadClick}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-700 border border-transparent rounded-md hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>Download PDF</span>
            </button>
          </div>
        </div>
      </div>

      {/* Prepared For Prompt Modal */}
      {showPreparedForPrompt && (
        <PreparedForPrompt
          onSubmit={handlePreparedForSubmit}
          onCancel={() => setShowPreparedForPrompt(false)}
        />
      )}

      {/* PDF Options Editor Modal */}
      {showOptionsEditor && (
        <PDFOptionsEditor
          exportOptions={exportOptions}
          customColumns={customColumns}
          onUpdate={async (options) => {
            if (onUpdateOptions) {
              await onUpdateOptions(options)
            }
            setShowOptionsEditor(false)
          }}
          onCancel={() => setShowOptionsEditor(false)}
        />
      )}

      {/* Bulk Edit Confirmation Modal */}
      {bulkEditConfirmation && (
        <BulkEditConfirmationModal
          isOpen={bulkEditConfirmation.isOpen}
          onClose={() => setBulkEditConfirmation(null)}
          onConfirm={handleBulkEditConfirm}
          field={bulkEditConfirmation.field}
          originalValue={bulkEditConfirmation.originalValue}
          newValue={bulkEditConfirmation.newValue}
          matchingCount={bulkEditConfirmation.matchingAircraft.length}
          currentAircraftSerial={bulkEditConfirmation.matchingAircraft.length > 0 ?
            previewData[Object.keys(previewData)[0]]?.find(row => row.aircraftId === bulkEditConfirmation.aircraftId)?.serial.split('\n')[0] || 'Unknown' :
            'Unknown'
          }
        />
      )}
    </Modal>
  )
}
